import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import OnboardingContext from '../context';
import SvgFormat from 'common/helpers/svgFormat';
import classNames from 'classnames';
import hexToRGBA from '~helpers/hex-to-rgba';
import event from '~helpers/mixpanel';
import { pageType } from '~constants/pages';

const useStyles = makeStyles(theme => ({
  wrap: {
    position: 'relative',
    width: '565px',
    padding: '16px 24px',
    border: `2px solid ${theme.colors.gray1}`,
    backgroundColor: theme.colors.gray1,
    cursor: 'pointer',
    borderRadius: theme.borderRadius.min,

    '&:not(:last-child)': {
      marginBottom: '20px',
    },
  },
  selectedWrap: {
    border: `2px solid ${theme.colors.mainGreen6}`,
    boxShadow: '0 12px 32px 2px rgba(107, 146, 243, 0.19)',
  },
  name: {
    ...theme.typography.h3,
    color: hexToRGBA(theme.colors.gray10, 0.85),
    marginBottom: '8px',
  },
  description: {
    fontSize: '12px',
    lineHeight: '20px',
    color: hexToRGBA(theme.colors.gray10, 0.65),
  },
  tickWrap: {
    position: 'absolute',
    top: '-16px',
    right: '-16px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '32px',
    height: '32px',
    border: `1px solid ${theme.colors.gray1}`,
    borderRadius: '50%',
    backgroundColor: theme.colors.mainGreen6,
  },
  tick: {
    width: '16px',
    height: '12px',
    fill: theme.colors.gray1,
  },
}));

const PropertyTypeItem = ({ propertyType }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const onboardingData = useContext(OnboardingContext);
  const [hovered, setHovered] = useState(false);

  const handleClickPropertyType = useCallback(() => {
    onboardingData?.setPropertyTypeSlug?.(propertyType?.slug);

    event('Clicked OP Property Type Page', {
      'Clicked Position': 'Property Type',
      'Property Type': propertyType?.slug,
      'Current Page Type': pageType.onBoardingPage,
    });
  }, [onboardingData, propertyType?.slug]);

  const isSelected = useMemo(() => {
    return propertyType?.slug === onboardingData?.propertyTypeSlug;
  }, [propertyType, onboardingData]);

  return (
    <div
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      className={classNames(classes.wrap, { [classes.selectedWrap]: isSelected || hovered })}
      onClick={handleClickPropertyType}
    >
      <If condition={isSelected}>
        <span className={classes.tickWrap}>
          <SvgFormat className={classes.tick} type="tick" />
        </span>
      </If>
      <div className={classes.name}>{t(propertyType.name)}</div>
      <div className={classes.description}>{propertyType.description}</div>
    </div>
  );
};

PropertyTypeItem.propTypes = {
  propertyType: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }),
};

PropertyTypeItem.defaultProps = {
  propertyType: {
    slug: '',
    name: '',
    description: '',
  },
};

export default React.memo(PropertyTypeItem);
